import React, { useCallback, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, EyebrowLG, HeadingXS, TitleLG, TitleSM } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { ValidNoFeesDiscount } from '@/services/Theatrical/queries'
import { useTranslate } from '@/utils/translate/translate-client'
import { PromoImage } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { useRestrictionCopy } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'
import { FlexibleBookingToolTip } from '../ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '../ToolTips/SpecificTimesToolTip'

interface NoFeesModalProps {
  open: boolean
  onClose: () => void
  backgroundImage: PromoImage
  accentColor: string
  isDarkMode: boolean
  code: string
  noFeesDiscount: ValidNoFeesDiscount
}

export const NoFeesModal = ({
  open,
  onClose,
  code,
  backgroundImage,
  accentColor,
  isDarkMode,
  noFeesDiscount,
}: NoFeesModalProps) => {
  const { t } = useTranslate('tickets')
  const [copiedCode, setCopiedCode] = useState(false)
  const restrictionCopy = useRestrictionCopy(noFeesDiscount)
  const textColor = isDarkMode ? 'core-gray-100' : 'core-gray-950'

  const handleCopyCode = useCallback(() => {
    if (code != null) {
      navigator.clipboard.writeText(code)
      setCopiedCode(true)
    }
  }, [code])

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      backgroundOverlayClassName="!bg-black/85"
      panelClasses={`bg-bottom bg-cover text-white !max-w-[380px] shadow-dark-4 rounded-3xl !p-6 !pt-10 relative text-center !overflow-visible`}
      transition="appear"
      className="z-[99999]"
    >
      <div className="absolute inset-0 overflow-hidden rounded-3xl">
        <div className="absolute inset-[-1px]">
          <Image
            src={backgroundImage.url}
            alt="Background Image"
            width={382}
            height={(382 * backgroundImage.height) / backgroundImage.width}
            className="rounded-3xl object-cover object-[bottom_center]"
          />
        </div>
      </div>
      <div>
        <div
          className={classNames(
            'absolute right-6 top-6 cursor-pointer rounded-full p-2',
            isDarkMode ? 'bg-white/10' : 'bg-black/10',
          )}
          onClick={onClose}
        >
          <CloseIcon color={isDarkMode ? 'white' : 'black'} size={18} />
        </div>
        <FlexColumn className="relative z-10 gap-6">
          <FlexColumn className="gap-1">
            <HeadingXS color={textColor} weight="bold" className="my-2 w-[70%] self-center">
              {t('noOnlineFeesNoHassleV1', 'No Online Fees. No Hassle.')}
            </HeadingXS>
            {restrictionCopy && (
              <FlexRow className="justify-center gap-1">
                <SpecificTimesToolTip buttonColor={isDarkMode ? 'white' : 'black'} copy={restrictionCopy}>
                  <TitleSM color={textColor}>{t('onSelectShowtimes', 'On Select Showtimes')}</TitleSM>
                </SpecificTimesToolTip>
              </FlexRow>
            )}
            <FlexibleBookingToolTip
              className="mx-auto w-fit"
              panelPosition="CENTER"
              buttonColor={isDarkMode ? 'core-gray-100' : 'core-gray-950'}
              buttonClassName={classNames(
                'focus-visible:outline-none focus-visible:ring-offset-transparent focus-visible:ring-transparent backdrop-blur-[24px] !backdrop-filter whitespace-nowrap',
                isDarkMode ? 'bg-white/10 text-core-gray-100' : 'bg-black/10 text-core-gray-950',
              )}
            />
          </FlexColumn>
          <FlexColumn className="w-fit self-center">
            <EyebrowLG color={textColor} weight={'semibold'} className="text-[0.938rem] uppercase">
              {t('usePromoCode', 'Use Promo Code')}
            </EyebrowLG>
            <div className="relative my-1 px-3 py-2 text-white" style={{ backgroundColor: accentColor }}>
              <TitleLG color="white" weight="bold" className={'relative'}>
                {code}
              </TitleLG>
            </div>

            <EyebrowLG color={textColor} weight="semibold" className="mt-0 text-[0.938rem] uppercase">
              {t('atCheckout', 'At Checkout')}
            </EyebrowLG>
          </FlexColumn>
          <FlexColumn className="items-center gap-3">
            <Button
              onClick={handleCopyCode}
              className="flex w-fit items-center gap-1"
              variant={isDarkMode ? 'white' : 'black'}
            >
              {copiedCode ? (
                <CheckIcon size={24} color={isDarkMode ? 'black' : 'white'} />
              ) : (
                <CopyIcon size={24} color={isDarkMode ? 'black' : 'white'} />
              )}
              {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
            </Button>
            <CaptionSM italic color="core-gray-600">
              {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
            </CaptionSM>
          </FlexColumn>
        </FlexColumn>
      </div>
    </Modal>
  )
}
